import { useEffect } from 'react';
import { Shield, Star, Leaf, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function About() {
  const { t } = useLanguage();

  useEffect(() => {
    document.title = `${t('about.title')} | Orange Onderhoudsbedrijf`;
  }, []);

  const values = [
    { icon: Shield, title: t('about.value1'), desc: t('about.value1Desc') },
    { icon: Star, title: t('about.value2'), desc: t('about.value2Desc') },
    { icon: Heart, title: t('about.value3'), desc: t('about.value3Desc') },
    { icon: Leaf, title: t('about.value4'), desc: t('about.value4Desc') },
  ];

  return (
    <div className="bg-[#F8FAFC] pt-20">
      {/* Header */}
      <section data-testid="about-header" className="bg-white border-b border-slate-100 px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">Orange Onderhoudsbedrijf</span>
          <h1 className="text-5xl md:text-6xl font-black text-[#0F172A] mt-3 mb-5">{t('about.title')}</h1>
          <p className="text-lg text-[#64748B] leading-relaxed">{t('about.subtitle')}</p>
        </div>
      </section>

      {/* Story */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">{t('about.storyTitle')}</span>
            <h2 className="text-4xl font-bold text-[#0F172A] mt-3 mb-6">{t('about.storyTitle')}</h2>
            <p className="text-[#64748B] leading-relaxed text-lg mb-6">{t('about.storyText')}</p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {[
                { num: '10+', label: 'Jaar Ervaring' },
                { num: '500+', label: 'Projecten' },
                { num: '100%', label: 'Klanttevredenheid' },
                { num: '24u', label: 'Reactietijd' },
              ].map((s, i) => (
                <div key={i} className="bg-white border border-slate-100 rounded-xl p-5 text-center">
                  <p className="text-2xl font-black text-[#FF5F00]">{s.num}</p>
                  <p className="text-xs text-[#64748B] font-medium mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80"
              alt="Professional maintenance team"
              className="rounded-2xl object-cover w-full h-96 shadow-xl"
            />
            <div className="absolute -bottom-5 -right-5 bg-[#FF5F00] rounded-2xl p-5 text-white shadow-xl hidden md:block">
              <p className="text-2xl font-black">Omar</p>
              <p className="text-orange-100 text-sm">Eigenaar & Oprichter</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section data-testid="about-mission" className="bg-[#0F172A] px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">Mission</span>
          <h2 className="text-4xl font-bold text-white mt-3 mb-6">{t('about.missionTitle')}</h2>
          <p className="text-slate-300 text-lg leading-relaxed">{t('about.missionText')}</p>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values" className="px-6 md:px-12 lg:px-24 py-16">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">Values</span>
          <h2 className="text-4xl font-bold text-[#0F172A] mt-3">{t('about.valuesTitle')}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v, i) => (
            <div key={i} data-testid={`value-${i}`} className="bg-white border border-slate-100 rounded-2xl p-7 text-center hover:border-orange-200 hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <v.icon size={28} strokeWidth={1.5} className="text-[#FF5F00]" />
              </div>
              <h3 className="font-bold text-[#0F172A] text-lg mb-2">{v.title}</h3>
              <p className="text-[#64748B] text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
